import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Stack,
  CircularProgress,
  Button,
  Skeleton,
  Slide,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getUser } from "../../redux/slices/AuthUser";
import { useChatPopup } from "./ChatPopupContext";
import { MessageBubble } from "./MessageBubble";
import { MessageComposer } from "./MessageComposer";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../axios/axiosInstance";

const POLL_INTERVAL = 5000;
const PAGE_SIZE = 30;

// Group messages by date (reused from ConversationView)
const groupByDate = (messages) => {
  const groups = [];
  let currentDate = null;
  let currentGroup = [];
  for (const msg of messages) {
    const msgDate = new Date(msg.createdAt).toDateString();
    if (msgDate !== currentDate) {
      if (currentGroup.length > 0) groups.push({ date: currentDate, messages: currentGroup });
      currentDate = msgDate;
      currentGroup = [msg];
    } else {
      currentGroup.push(msg);
    }
  }
  if (currentGroup.length > 0) groups.push({ date: currentDate, messages: currentGroup });
  return groups;
};

const DateDivider = ({ dateStr }) => {
  const { t } = useTranslation("chat");
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();
  let label;
  if (dateStr === today) label = t("today");
  else if (dateStr === yesterday) label = t("yesterday");
  else label = new Date(dateStr).toLocaleDateString([], { month: "short", day: "numeric" });

  return (
    <Box sx={{ display: "flex", justifyContent: "center", my: 1.5 }}>
      <Typography
        variant="caption"
        sx={{
          backgroundColor: "rgba(7,31,40,0.06)",
          borderRadius: "12px",
          px: 1.5,
          py: 0.25,
          fontSize: 11,
          fontWeight: 600,
          color: "text.secondary",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

let clientIdCounter = 0;
const generateClientId = () => `popup_${Date.now()}_${++clientIdCounter}`;

const SlideTransition = (props) => <Slide direction="up" {...props} />;

export const ChatPopup = () => {
  const { open, chatTarget, closeChat } = useChatPopup();
  const { t } = useTranslation("chat");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const userId = useSelector(getUser)?.id;

  // Conversation state
  const [conversation, setConversation] = useState(null);
  const [loadingConv, setLoadingConv] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [sending, setSending] = useState(false);
  const [moderationError, setModerationError] = useState(null);
  const [error, setError] = useState(null);

  const scrollBottomRef = useRef(null);
  const prevMsgCountRef = useRef(0);
  const pollRef = useRef(null);

  // Create or find conversation when popup opens
  useEffect(() => {
    if (!open || !chatTarget?.productId || !userId) {
      return;
    }

    let cancelled = false;
    setLoadingConv(true);
    setConversation(null);
    setMessages([]);
    setError(null);

    axiosInstance
      .post("/messages/conversations/product", { productId: chatTarget.productId })
      .then((res) => {
        if (!cancelled) {
          setConversation(res.data.conversation);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.error || "Failed to open chat");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingConv(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, chatTarget?.productId, userId]);

  // Fetch messages when conversation is available
  const fetchMessages = useCallback(
    async (silent = false) => {
      if (!conversation?._id) return;
      if (!silent) setLoadingMsgs(true);
      try {
        const res = await axiosInstance.get(
          `/messages/conversations/${conversation._id}/messages`,
          { params: { limit: PAGE_SIZE } }
        );
        const fetched = res.data.messages || [];
        if (silent) {
          setMessages((prev) => {
            const existingIds = new Set(prev.map((m) => m._id));
            const newMsgs = fetched.filter((m) => !existingIds.has(m._id));
            if (newMsgs.length === 0) return prev;
            return [...prev, ...newMsgs];
          });
        } else {
          setMessages(fetched);
          setHasMore(fetched.length >= PAGE_SIZE);
        }
      } catch {
        // silent fail for polling
      } finally {
        if (!silent) setLoadingMsgs(false);
      }
    },
    [conversation?._id]
  );

  useEffect(() => {
    if (conversation?._id) {
      fetchMessages();
    }
  }, [conversation?._id, fetchMessages]);

  // Polling for new messages
  useEffect(() => {
    if (!conversation?._id || !open) return;
    pollRef.current = setInterval(() => fetchMessages(true), POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [conversation?._id, open, fetchMessages]);

  // Clean up polling on close
  useEffect(() => {
    if (!open) {
      clearInterval(pollRef.current);
    }
  }, [open]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (messages.length > prevMsgCountRef.current && messages.length > 0) {
      setTimeout(() => {
        scrollBottomRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 50);
    }
    prevMsgCountRef.current = messages.length;
  }, [messages.length]);

  // Mark as read
  useEffect(() => {
    if (conversation?._id && messages.length > 0) {
      axiosInstance.put(`/messages/conversations/${conversation._id}/read`).catch(() => {});
    }
  }, [conversation?._id, messages.length]);

  // Load older messages
  const loadMore = useCallback(async () => {
    if (!conversation?._id || loadingMore || !hasMore || messages.length === 0) return;
    setLoadingMore(true);
    try {
      const oldest = messages[0];
      const res = await axiosInstance.get(
        `/messages/conversations/${conversation._id}/messages`,
        { params: { before: oldest.createdAt, limit: PAGE_SIZE } }
      );
      const older = res.data.messages || [];
      setHasMore(older.length >= PAGE_SIZE);
      if (older.length > 0) {
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => m._id));
          return [...older.filter((m) => !ids.has(m._id)), ...prev];
        });
      }
    } catch {
      // ignore
    } finally {
      setLoadingMore(false);
    }
  }, [conversation?._id, loadingMore, hasMore, messages]);

  // Send message
  const handleSend = useCallback(
    async (body) => {
      if (!conversation?._id) return;
      const clientMessageId = generateClientId();

      // Optimistic add
      setMessages((prev) => [
        ...prev,
        {
          _id: clientMessageId,
          clientMessageId,
          conversationId: conversation._id,
          senderId: userId,
          body,
          messageType: "text",
          isRead: false,
          createdAt: new Date().toISOString(),
          _pending: true,
        },
      ]);

      setSending(true);
      try {
        const res = await axiosInstance.post(
          `/messages/conversations/${conversation._id}/messages`,
          { body, clientMessageId }
        );
        const saved = res.data.message;
        setMessages((prev) => {
          const idx = prev.findIndex((m) => m.clientMessageId === clientMessageId);
          if (idx >= 0) {
            const updated = [...prev];
            updated[idx] = saved;
            return updated;
          }
          if (prev.some((m) => m._id === saved._id)) return prev;
          return [...prev, saved];
        });
        setModerationError(null);
      } catch (err) {
        const errorData = err.response?.data;
        // Remove optimistic message
        setMessages((prev) => prev.filter((m) => m.clientMessageId !== clientMessageId));
        if (errorData?.blocked) {
          setModerationError(errorData.error || t("moderation.blocked"));
        } else {
          // Re-add as failed
          setMessages((prev) => [
            ...prev,
            {
              _id: clientMessageId,
              clientMessageId,
              conversationId: conversation._id,
              senderId: userId,
              body,
              messageType: "text",
              isRead: false,
              createdAt: new Date().toISOString(),
              _failed: true,
            },
          ]);
        }
        throw errorData || { error: "Failed to send message" };
      } finally {
        setSending(false);
      }
    },
    [conversation?._id, userId, t]
  );

  const handleReport = useCallback(async (messageId) => {
    try {
      await axiosInstance.post(`/messages/${messageId}/report`);
    } catch {
      // ignore
    }
  }, []);

  const handleExpandToFullChat = useCallback(() => {
    if (conversation?._id) {
      closeChat();
      navigate(`/chat/${conversation._id}`);
    }
  }, [conversation?._id, closeChat, navigate]);

  const messageGroups = useMemo(() => groupByDate(messages), [messages]);

  const ownerName = chatTarget?.ownerName || conversation?.otherParticipant?.displayName || "";
  const productName = chatTarget?.productName || conversation?.product?.name || "";
  const productPicture =
    chatTarget?.productPicture || conversation?.product?.picture || null;

  // Other user info for the "seen" avatar
  const otherUser = useMemo(() => {
    // Try chatTarget first, then conversation, then extract from received messages
    const name = chatTarget?.ownerName || conversation?.otherParticipant?.displayName || "";
    const pic = chatTarget?.ownerProfilePicture || conversation?.otherParticipant?.profilePicture || null;
    if (pic || name) return { displayName: name, profilePicture: pic };
    // Fallback: find a received message and use its sender info
    const received = messages.find((m) => {
      const sid = m.senderId?._id || m.senderId;
      return sid !== userId;
    });
    if (received?.senderId?.profilePicture || received?.senderId?.displayName) {
      return { displayName: received.senderId.displayName, profilePicture: received.senderId.profilePicture };
    }
    return { displayName: name, profilePicture: null };
  }, [chatTarget, conversation, messages, userId]);

  // Find last own message that was read (for "seen" avatar)
  const lastReadOwnMsgId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      const senderId = msg.senderId?._id || msg.senderId;
      if (senderId === userId && msg.isRead && !msg._pending && !msg._failed) {
        return msg._id;
      }
    }
    return null;
  }, [messages, userId]);

  return (
    <Dialog
      open={open}
      onClose={closeChat}
      TransitionComponent={SlideTransition}
      maxWidth={false}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          position: isMobile ? undefined : "fixed",
          bottom: isMobile ? undefined : 16,
          right: isMobile ? undefined : 16,
          m: 0,
          width: isMobile ? "100%" : 420,
          height: isMobile ? "100%" : 560,
          maxHeight: isMobile ? "100%" : "calc(100vh - 100px)",
          borderRadius: isMobile ? 0 : "16px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        },
      }}
      slotProps={{
        backdrop: { sx: { backgroundColor: isMobile ? "rgba(0,0,0,0.5)" : "transparent" } },
      }}
      disableScrollLock={!isMobile}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          background: "linear-gradient(135deg, #FE1F02 0%, #C91906 100%)",
          color: "white",
          flexShrink: 0,
        }}
      >
        {/* Product thumbnail */}
        {productPicture && (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "8px",
              overflow: "hidden",
              flexShrink: 0,
              border: "2px solid rgba(255,255,255,0.3)",
            }}
          >
            <Box
              component="img"
              src={productPicture}
              alt={productName}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 15,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {ownerName}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              opacity: 0.85,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {productName}
          </Typography>
        </Box>

        <IconButton
          size="small"
          onClick={handleExpandToFullChat}
          sx={{ color: "white", opacity: 0.8, "&:hover": { opacity: 1 } }}
          title="Open full chat"
        >
          <OpenInFullIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <IconButton
          size="small"
          onClick={closeChat}
          sx={{ color: "white", opacity: 0.8, "&:hover": { opacity: 1 } }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Body */}
      {loadingConv ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={32} />
        </Box>
      ) : error ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 3,
          }}
        >
          <Typography color="error" sx={{ textAlign: "center" }}>
            {error}
          </Typography>
        </Box>
      ) : (
        <>
          {/* Messages area */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              py: 1,
              display: "flex",
              flexDirection: "column",
              backgroundColor: "background.default",
            }}
          >
            {/* Load more */}
            {hasMore && !loadingMsgs && (
              <Box sx={{ textAlign: "center", py: 1 }}>
                <Button
                  onClick={loadMore}
                  disabled={loadingMore}
                  size="small"
                  sx={{ textTransform: "none", fontSize: 12 }}
                >
                  {loadingMore && <CircularProgress size={14} sx={{ mr: 0.5 }} />}
                  {t("loadMore")}
                </Button>
              </Box>
            )}

            {loadingMsgs ? (
              <Stack spacing={1} sx={{ p: 2 }}>
                {[1, 2, 3].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      justifyContent: i % 2 === 0 ? "flex-end" : "flex-start",
                      px: 1,
                    }}
                  >
                    <Skeleton
                      variant="rounded"
                      width={i % 3 === 0 ? 160 : 220}
                      height={40}
                      sx={{ borderRadius: "16px" }}
                    />
                  </Box>
                ))}
              </Stack>
            ) : messages.length === 0 ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: 1,
                  p: 3,
                }}
              >
                <Typography sx={{ fontSize: 32 }}>💬</Typography>
                <Typography
                  color="text.secondary"
                  sx={{ fontSize: 14, textAlign: "center" }}
                >
                  {t("empty.noMessagesYet")}
                </Typography>
              </Box>
            ) : (
              messageGroups.map((group) => (
                <Box key={group.date}>
                  <DateDivider dateStr={group.date} />
                  {group.messages.map((msg) => {
                    const senderId = msg.senderId?._id || msg.senderId;
                    const isOwn = senderId === userId;
                    return (
                      <MessageBubble
                        key={msg._id}
                        message={msg}
                        isOwn={isOwn}
                        onReport={handleReport}
                        showSeenAvatar={isOwn && msg._id === lastReadOwnMsgId}
                        seenByUser={otherUser}
                      />
                    );
                  })}
                </Box>
              ))
            )}
            <div ref={scrollBottomRef} />
          </Box>

          {/* Composer */}
          <MessageComposer
            onSend={handleSend}
            disabled={!conversation}
            sending={sending}
            contactLocked={true}
            isReadonly={conversation?.status === "readonly"}
            moderationError={moderationError}
            onClearModError={() => setModerationError(null)}
          />
        </>
      )}
    </Dialog>
  );
};
